import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import {
  selectScopedValues,
  setSaveError,
  setScopedValues,
} from '../../../../store/slices/peopleConfigSlice';
import { cadenceToRedux, escalationToRedux } from '../../../../store/thunks/peopleConfigThunks';
import AdminButton from '../../../Admin/components/AdminButton';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import ConfigCard from '../components/ConfigCard';
import ConfigGhostButton from '../components/ConfigGhostButton';
import ConfigConfirmModal from '../components/ConfigConfirmModal';
import ConfigTable from '../components/ConfigTable';
import OrgDeptPicker from '../components/OrgDeptPicker';
import TabBody from '../components/TabBody';
import { usePeopleConfig } from '../PeopleConfigContext';
import { ConfigHistoryEntry, ScopedField, ScopedNumbersConfig, ScopedSavePayload } from '../configTypes';
import {
  getApiErrorMessage,
  getScopeLocations,
  locationKey,
  nationalKey,
  pluralize,
} from '../configUtils';
import ScopedNumbersDrawer from './ScopedNumbersDrawer';
import {
  deleteCadenceOverride,
  deleteEscalationOverride,
  saveCadenceConfig,
  saveEscalationConfig,
} from '../../../../api/peopleConfigApi';

interface ScopedNumbersTabProps {
  config: ScopedNumbersConfig;
}

type EditState = { mode: 'national' | 'location'; loc?: string } | null;

/**
 * Numeric settings set nationally for an org and department, with optional location overrides.
 * Used by Cadence and Escalation.
 */
const ScopedNumbersTab: React.FC<ScopedNumbersTabProps> = ({ config }) => {
  const ctx = usePeopleConfig();
  const { org, dept, locations, status, user } = ctx;
  const dispatch = useDispatch();
  const values = useSelector((state: RootState) => selectScopedValues(state, config.sectionKey));
  const [edit, setEdit] = useState<EditState>(null);
  const [confirm, setConfirm] = useState<ScopedSavePayload | null>(null);
  const [saving, setSaving] = useState(false);

  const nationalId = nationalKey(org, dept);
  const nat = values[nationalId] ?? {};
  const scopeLocations = getScopeLocations(locations, org, dept);
  const show = (f: ScopedField, v?: number) => (v == null ? 'Not set' : config.value(f, v));
  // An override with no values left is removed from the list.
  const overrides = scopeLocations.filter((l) =>
    config.fields.some((f) => values[locationKey(l.name, dept)]?.[f.key] != null)
  );

  const publish = ({ id, loc, form, initial }: ScopedSavePayload) =>
    ctx.guard(
      async () => {
        const isLocation = id.startsWith('location:');
        const cur = { ...(values[id] ?? {}) };
        const entries: Omit<ConfigHistoryEntry, 'at' | 'by'>[] = [];

        config.fields.forEach((f) => {
          if (form[f.key] === initial[f.key]) return;
          const next = form[f.key] === '' ? null : Number(form[f.key]);
          const prior =
            cur[f.key] != null
              ? pluralize(cur[f.key], f.unit)
              : isLocation && nat[f.key] != null
                ? `National ${pluralize(nat[f.key], f.unit)}`
                : 'Not set';
          if (next === null) delete cur[f.key];
          else cur[f.key] = next;
          entries.push({
            section: config.section,
            setting: f.row,
            scope: isLocation ? `${loc} / ${dept}` : `${org} ${dept}`,
            prior,
            next: next === null ? `National ${config.noun}` : pluralize(next, f.unit),
            effective: config.effective,
          });
        });

        if (entries.length === 0) {
          setConfirm(null);
          setEdit(null);
          return;
        }

        setSaving(true);
        try {
          // Build the save request and call the appropriate API.
          if (config.sectionKey === 'cadence') {
            const req = {
              org,
              department: dept,
              location: isLocation ? loc : null,
              pulseWeeks: cur.pulseWeeks != null ? Number(cur.pulseWeeks) : null,
              obsPeriods: cur.obsPeriods != null ? Number(cur.obsPeriods) : null,
              updatedBy: user,
            };
            // If all fields are deleted for a location override, delete the override.
            if (isLocation && Object.keys(cur).length === 0) {
              await deleteCadenceOverride(org, dept, loc!);
            } else {
              const updated = await saveCadenceConfig(req);
              dispatch(setScopedValues({ section: 'cadence', values: cadenceToRedux(updated) }));
            }
            if (isLocation && Object.keys(cur).length === 0) {
              const nextValues = { ...values };
              delete nextValues[id];
              dispatch(setScopedValues({ section: 'cadence', values: nextValues }));
            }
          } else {
            // escalation
            const req = {
              org,
              department: dept,
              location: isLocation ? loc : null,
              escalate: cur.escalate != null ? Number(cur.escalate) : null,
              forceClose: cur.forceClose != null ? Number(cur.forceClose) : null,
              updatedBy: user,
            };
            if (isLocation && Object.keys(cur).length === 0) {
              await deleteEscalationOverride(org, dept, loc!);
              const nextValues = { ...values };
              delete nextValues[id];
              dispatch(setScopedValues({ section: 'escalation', values: nextValues }));
            } else {
              const updated = await saveEscalationConfig(req);
              dispatch(setScopedValues({ section: 'escalation', values: escalationToRedux(updated) }));
            }
          }

          ctx.log(entries);
          setConfirm(null);
          setEdit(null);
        } catch (err) {
          console.error(`[ScopedNumbers] Failed to save ${config.sectionKey}:`, err);
          // Redux is untouched — the save failed. The confirm dialog closes and the drawer stays
          // open with the user's input, showing the error so they can retry.
          setConfirm(null);
          dispatch(setSaveError(getApiErrorMessage(err)));
        } finally {
          setSaving(false);
        }
      },
      () => setConfirm(null)
    );

  return (
    <TabBody>
      <OrgDeptPicker />

      <ConfigCard
        title={config.nationalTitle}
        subtitle={config.nationalSub(org, dept)}
        status={status}
        onRetry={ctx.retry}
        table
        action={
          <AdminButton
            label={config.editLabel}
            small
            disabled={status === 'loading'}
            onPress={() => setEdit({ mode: 'national' })}
          />
        }
      >
        <ConfigTable
          columns={[
            { key: 'a', header: config.colHead[0] },
            // Mockup: plain text value column, left aligned.
            { key: 'b', header: config.colHead[1], align: 'left' },
          ]}
          rows={config.fields.map((f) => ({
            key: f.key,
            cells: [
              <Text key="r" style={styles.strong}>{f.row}</Text>,
              nat[f.key] != null ? show(f, nat[f.key]) : <Text key="v" style={styles.muted}>Not set</Text>,
            ],
          }))}
        />
      </ConfigCard>

      <ConfigCard
        title="Location Overrides"
        subtitle={`Optional. A location value replaces the national ${config.noun} for ${dept} at that location. Grey values come from the national ${config.noun}.`}
        status={status}
        onRetry={ctx.retry}
        table
        action={
          <AdminButton
            label="Add Override"
            variant="secondary"
            small
            disabled={status === 'loading' || scopeLocations.length === 0}
            onPress={() => setEdit({ mode: 'location' })}
          />
        }
      >
        <ConfigTable
          columns={[
            { key: 'loc', header: 'Location' },
            ...config.fields.map((f) => ({ key: f.key, header: f.row })),
            { key: 'act', header: 'Actions', hideHeader: true, flex: 0.5 },
          ]}
          emptyMessage={
            scopeLocations.length
              ? `No location overrides. Every location uses the national ${config.noun}.`
              : `No ${org} locations have ${dept} yet.`
          }
          rows={overrides.map((l) => {
            const row = values[locationKey(l.name, dept)] ?? {};
            return {
              key: l.name,
              cells: [
                <Text key="l" style={styles.strong}>{l.name}</Text>,
                ...config.fields.map((f) =>
                  row[f.key] != null ? (
                    <Text key={f.key} style={styles.own}>{show(f, row[f.key])}</Text>
                  ) : (
                    <View key={f.key}>
                      <Text style={styles.inherit}>{show(f, nat[f.key])}</Text>
                      <Text style={styles.inheritTag}>National</Text>
                    </View>
                  )
                ),
                <ConfigGhostButton
                  key="e"
                  label="Edit"
                  onPress={() => setEdit({ mode: 'location', loc: l.name })}
                />,
              ],
            };
          })}
        />
      </ConfigCard>

      {edit ? (
        <ScopedNumbersDrawer
          key={`${edit.mode}:${org}:${dept}:${edit.loc ?? 'new'}`}
          config={config}
          org={org}
          dept={dept}
          mode={edit.mode}
          startLoc={edit.loc}
          locations={scopeLocations}
          values={values}
          conflict={ctx.conflict}
          onClose={() => setEdit(null)}
          onSave={(payload) => (config.confirm ? setConfirm(payload) : publish(payload))}
        />
      ) : null}

      {confirm && config.confirm ? (
        <ConfigConfirmModal
          title={config.confirm.title}
          message={config.confirm.body}
          confirmLabel={saving ? 'Saving…' : 'Save changes'}
          onCancel={() => setConfirm(null)}
          onConfirm={() => publish(confirm)}
        />
      ) : null}
    </TabBody>
  );
};

const styles = StyleSheet.create({
  strong: { fontSize: 14, fontFamily: FontFamily.bold, color: AdminColors.textPrimary },
  muted: { fontSize: 14, fontFamily: FontFamily.regular, color: AdminColors.textMuted },
  own: { fontSize: 14, fontFamily: FontFamily.bold, color: AdminColors.textPrimary },
  inherit: { fontSize: 14, fontFamily: FontFamily.regular, color: AdminColors.textMuted },
  inheritTag: { fontSize: 10, fontFamily: FontFamily.regular, color: AdminColors.gray300 },
});

export default ScopedNumbersTab;
